import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import {
  Lock,
  Menu,
  X,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Instagram,
  Youtube,
  ChevronDown,
  Sparkles,
  Car,
  ShoppingCart,
  Wrench,
  Calendar,
  Sun,
  Moon,
  Clock,
  Shield,
  CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/contexts/ThemeContext';

const LandingVanguard = () => {
  const { theme, toggleTheme } = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hoveredBrand, setHoveredBrand] = useState(null);
  const [seguroForm, setSeguroForm] = useState({ nombre: '', telefono: '', email: '', marca: '', modelo: '', anio: '', cobertura: '' });
  const [seguroEnvio, setSeguroEnvio] = useState('idle'); // idle | loading | ok | error
  const containerRef = useRef(null);

  // Slides con imágenes del concesionario
  const slides = [
    {
      image: process.env.PUBLIC_URL + '/hero/Byd_Yuan_Pro_Desktop.jpg',
      imageMobile: process.env.PUBLIC_URL + '/hero/Byd_Yuan_Pro_Mobile.jpg',
      title: 'BYD YUAN PRO',
      subtitle: 'Futuro Eléctrico',
      description: '100% Eléctrico | 380km Autonomía | Carga Rápida DC',
      brand: 'BYD',
      brandLogo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/rqt16dv8_Logo_BYD_Blanco_para_fondo_oscuro.png',
      brandLogoSize: 'small',
      color: '#3889b1',
      phone: '59892394511'
    },
    {
      image: process.env.PUBLIC_URL + '/hero/Peugueot_208_Style_Desktop.jpg',
      imageMobile: process.env.PUBLIC_URL + '/hero/Peugueot_208_Style_Mobile.jpg',
      title: 'PEUGEOT 208',
      subtitle: 'Elegancia Francesa',
      description: 'Diseño Francés | i-Cockpit 3D | Performance Ágil',
      brand: 'PEUGEOT',
      brandLogo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/usltsakg_Logo_Peugeot_Blanco_para_fondo_oscuro.png',
      color: '#c8d6e5',
      phone: '59892487711'
    },
    {
      image: process.env.PUBLIC_URL + '/hero/souest_S06_Desktop.jpg',
      imageMobile: process.env.PUBLIC_URL + '/hero/souest_S06_Mobile.jpg',
      title: 'SOUEAST S06',
      subtitle: 'Estilo y Modernidad',
      description: 'SUV Compacta | Diseño Contemporáneo | Tecnología Avanzada',
      brand: 'SOUEAST',
      brandLogo: 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Soueast_Logo.png',
      brandLogoFilter: 'brightness(0) invert(1)',
      color: '#ebdc4f',
      phone: '59891200262'
    },
    {
      image: process.env.PUBLIC_URL + '/hero/Geely_EX5_Desktop.jpg',
      imageMobile: process.env.PUBLIC_URL + '/hero/Geely_EX5_Mobile.jpg',
      title: 'GEELY EX5',
      subtitle: 'Potencia en la Ruta',
      description: 'SUV Premium | Autonomía Extendida | Confort Total',
      brand: 'GEELY',
      brandLogo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/925mkif1_Logo_Geely_Blanco_para_fondo_oscuro.png',
      color: '#70839a',
      phone: '59891200262'
    },
    {
      image: process.env.PUBLIC_URL + '/hero/Jeotur_X70_desktop.jpg',
      imageMobile: process.env.PUBLIC_URL + '/hero/Jeotur_X70_Mobile.jpg',
      title: 'JETOUR X70',
      subtitle: 'Aventura Familiar',
      description: 'SUV Familiar | 7 Pasajeros | Equipamiento Completo',
      brand: 'JETOUR',
      brandLogo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/0zf9gbf9_Logo_Jetour_Blanco_para_fondo_oscuro.png',
      color: '#848893',
      phone: '59891200262'
    }
  ];

  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);

  // Auto advance con transición mejorada
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  // Fetch featured vehicles from multiavisos
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/multiavisos/vehiculos`);
        const data = await response.json();
        const vehicles = data.Vehicles || [];

        // Filter featured and map to landing page structure
        const featured = vehicles
          .filter(v => v.Featured === "true" || v.Featured === true)
          .map(v => ({
            id: v.VehicleId,
            name: `${v.Brand} ${v.Model}`,
            category: v.Version || v.BodyType || 'Vehículo Destacado',
            price: v.Price ? `${v.Price.Currency} ${v.Price.Value.toLocaleString()}` : 'Consultar',
            year: v.Year,
            image: (v.Images && v.Images.length > 0)
              ? `${process.env.REACT_APP_BACKEND_URL}/api/multiavisos/images?url=${encodeURIComponent(v.Images[0])}`
              : 'https://images.unsplash.com/photo-1623869675781-80aa31012a5a?w=400&h=300&fit=crop',
            specs: {
              motor: v.Motor || 'N/A',
              autonomia: v.Range?.Display || 'N/A',
              aceleracion: 'N/A',
              bateria: v.BateryCapacity?.Display || 'N/A',
              traccion: v.Traction || 'N/A',
              extras: v.ComfortSpecs || []
            }
          }));
        setFeaturedVehicles(featured);
      } catch (error) {
        console.error("Error fetching featured vehicles:", error);
      } finally {
        setLoadingFeatured(false);
      }
    };
    fetchFeatured();
  }, []);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setHoveredBrand(null);
    };

    if (hoveredBrand) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [hoveredBrand]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div ref={containerRef} className="relative bg-background overflow-hidden transition-colors duration-500">
      {/* Cursor removed */}

      {/* Navbar */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5, type: "spring" }}
        className="fixed top-0 left-0 right-0 z-40 backdrop-blur-2xl border-b transition-colors duration-500"
        style={{
          background: theme === 'dark'
            ? 'rgba(0, 0, 0, 0.2)'
            : 'rgba(255, 255, 255, 0.85)',
          borderColor: theme === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.1)'
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <Link to="/" className="flex items-center">
                <img
                  src={`${process.env.PUBLIC_URL}/logo-amaya.svg`}
                  alt="Amaya Motors"
                  className="h-10 w-auto"
                />
              </Link>
            </motion.div>

            {/* Desktop Menu con efectos premium ultra-refinados */}
            <div className="hidden md:flex items-center gap-1">
              {[
                { label: 'Marcas', href: '#marcas' },
                { label: 'Vehículos', href: '/vehiculos', isLink: true },
                { label: 'Agenda Service', href: '#service' },
                { label: 'Seguros', href: '#seguros' },
                { label: 'Sucursales', href: '#sucursales' },
                { label: 'Contacto', href: '#contacto' }
              ].map((item, index) => (
                <div key={item.label} className="flex items-center">
                  {item.isLink ? (
                    <Link
                      to={item.href}
                      className="relative px-4 py-2 text-sm font-medium group overflow-hidden"
                      style={{
                        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.8)'
                      }}
                    >
                      <span className="relative z-10 tracking-wide hover:text-primary transition-colors">{item.label}</span>
                    </Link>
                  ) : (
                    <motion.a
                      href={item.href}
                      className="relative px-4 py-2 text-sm font-medium group overflow-hidden"
                      style={{
                        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.8)'
                      }}
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index, type: "spring" }}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span className="relative z-10 tracking-wide">{item.label}</span>

                      {/* Efecto underline animado con gradiente dinámico */}
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-cyan-400 to-blue-500"
                        initial={{ scaleX: 0, originX: 0 }}
                        whileHover={{ scaleX: 1 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                      />

                      {/* Efecto shimmer sutil */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full"
                        initial={{ x: "-100%" }}
                        whileHover={{ x: "100%" }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                      />
                    </motion.a>
                  )}

                  {/* Separador vertical animado y elegante */}
                  {index < 4 && (
                    <motion.div
                      className="h-5 w-px bg-gradient-to-b from-transparent via-white/20 to-transparent mx-1"
                      initial={{ scaleY: 0, opacity: 0 }}
                      animate={{ scaleY: 1, opacity: 0.5 }}
                      transition={{
                        duration: 0.5,
                        delay: 0.1 * index + 0.3,
                        type: "spring",
                        stiffness: 100
                      }}
                      whileHover={{ scaleY: 1.3, opacity: 0.8 }}
                    />
                  )}
                </div>
              ))}

              {/* Botón Theme Toggle ultra-refinado */}
              <motion.button
                onClick={toggleTheme}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.1, type: "spring" }}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.9 }}
                className="relative p-2.5 transition-colors duration-300 group"
                style={{
                  color: theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.7)'
                }}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <Sun className="h-4 w-4 relative z-10 transition-transform duration-300 group-hover:rotate-12" />
                ) : (
                  <Moon className="h-4 w-4 relative z-10 transition-transform duration-300 group-hover:rotate-12" />
                )}

                {/* Círculo de fondo con glassmorphism */}
                <motion.div
                  className="absolute inset-0 rounded-full bg-white/5 backdrop-blur-sm"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileHover={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />

                {/* Anillo pulsante en hover */}
                <motion.div
                  className="absolute inset-0 rounded-full border border-primary/0 group-hover:border-primary/40"
                  initial={{ scale: 0.8 }}
                  whileHover={{ scale: 1.3, opacity: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />

                {/* Glow sutil */}
                <motion.div
                  className="absolute inset-0 rounded-full bg-primary/0 blur-md group-hover:bg-primary/20"
                  transition={{ duration: 0.3 }}
                />
              </motion.button>

              {/* Separador especial premium antes del botón staff */}
              <motion.div
                className="mx-3 h-6 w-px bg-gradient-to-b from-transparent via-primary/40 to-transparent relative"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-400/60 to-transparent"
                  animate={{
                    opacity: [0.3, 0.7, 0.3],
                    scaleY: [0.8, 1, 0.8]
                  }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                />
              </motion.div>

              {/* Botón staff ultra-refinado y discreto */}
              <a href="https://usados.amaya.com.uy">
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.2, type: "spring" }}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                  className="relative p-2.5 transition-colors duration-300 group"
                  style={{
                    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.7)'
                  }}
                >
                  <Lock className="h-4 w-4 relative z-10 transition-transform duration-300 group-hover:rotate-12" />

                  {/* Círculo de fondo con glassmorphism */}
                  <motion.div
                    className="absolute inset-0 rounded-full bg-white/5 backdrop-blur-sm"
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileHover={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  />

                  {/* Anillo pulsante en hover */}
                  <motion.div
                    className="absolute inset-0 rounded-full border border-primary/0 group-hover:border-primary/40"
                    initial={{ scale: 0.8 }}
                    whileHover={{ scale: 1.3, opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />

                  {/* Glow sutil */}
                  <motion.div
                    className="absolute inset-0 rounded-full bg-primary/0 blur-md group-hover:bg-primary/20"
                    transition={{ duration: 0.3 }}
                  />
                </motion.button>
              </a>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
              style={{ color: theme === 'dark' ? 'white' : 'black' }}
            >
              <AnimatePresence mode="wait">
                {mobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                  >
                    <X className="h-6 w-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                  >
                    <Menu className="h-6 w-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="md:hidden backdrop-blur-xl border-t overflow-hidden"
              style={{
                background: theme === 'dark'
                  ? 'rgba(0, 0, 0, 0.95)'
                  : 'rgba(255, 255, 255, 0.95)',
                borderColor: theme === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.1)'
              }}
            >
              <div className="px-6 py-6 space-y-2">
                {[
                  { label: 'Marcas', href: '#marcas' },
                  { label: 'Vehículos', href: '/vehiculos', isLink: true },
                  { label: 'Agenda Service', href: '#service' },
                  { label: 'Seguros', href: '#seguros' },
                  { label: 'Sucursales', href: '#sucursales' },
                  { label: 'Contacto', href: '#contacto' }
                ].map((item, index) => (
                  item.isLink ? (
                    <Link
                      key={item.label}
                      to={item.href}
                      className="block py-3 px-4 rounded-lg transition-all duration-200"
                      style={{
                        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                      }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="text-base font-medium">{item.label}</span>
                    </Link>
                  ) : (
                    <motion.a
                      key={item.label}
                      href={item.href}
                      initial={{ x: -50, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: -50, opacity: 0 }}
                      transition={{
                        delay: index * 0.05,
                        type: "spring",
                        stiffness: 100,
                        damping: 15
                      }}
                      whileTap={{ scale: 0.95 }}
                      className="block py-3 px-4 rounded-lg transition-all duration-200 border border-transparent"
                      style={{
                        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="text-base font-medium">{item.label}</span>
                    </motion.a>
                  )
                ))}

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="pt-4 mt-4 border-t space-y-2"
                  style={{
                    borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <motion.button
                    onClick={toggleTheme}
                    whileTap={{ scale: 0.95 }}
                    className="w-full flex items-center gap-3 py-3 px-4 rounded-lg transition-all duration-200"
                    style={{
                      color: theme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {theme === 'dark' ? (
                      <>
                        <Sun className="h-5 w-5" />
                        <span className="text-base font-medium">Modo Claro</span>
                      </>
                    ) : (
                      <>
                        <Moon className="h-5 w-5" />
                        <span className="text-base font-medium">Modo Oscuro</span>
                      </>
                    )}
                  </motion.button>

                  <a href="https://usados.amaya.com.uy" onClick={() => setMobileMenuOpen(false)}>
                    <motion.div
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center gap-3 py-3 px-4 rounded-lg transition-all duration-200"
                      style={{
                        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Lock className="h-5 w-5" />
                      <span className="text-base font-medium">Acceso Staff</span>
                    </motion.div>
                  </a>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Hero Section con Slider VANGUARDISTA */}
      <section className="relative h-screen overflow-hidden">
        <AnimatePresence mode="sync">
          <motion.div
            key={currentSlide}
            className="absolute inset-0"
          >
            {/* Background con efecto parallax multi-capa */}
            <motion.div
              className="absolute inset-0"
              initial={{ scale: 1.2 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 1.2, ease: [0.43, 0.13, 0.23, 0.96] }}
            >
              {/* Layer 1 - Imagen de fondo */}
              <motion.div
                className="absolute inset-0"
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -100, opacity: 0 }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
              >
                <picture style={{ display: 'contents' }}>
                  <source media="(max-width: 767px)" srcSet={slides[currentSlide].imageMobile} />
                  <img
                    src={slides[currentSlide].image}
                    alt={slides[currentSlide].title}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: 'center' }}
                  />
                </picture>
              </motion.div>

              {/* Overlay desktop: gradiente lateral */}
              <motion.div
                className="absolute inset-0 hidden md:block"
                style={{
                  background: `radial-gradient(circle at 30% 50%, ${slides[currentSlide].color}15 0%, transparent 50%),
                               linear-gradient(to right, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 40%, transparent 100%)`
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              />
              {/* Overlay mobile: gradiente top-to-bottom sin sombra lateral */}
              <motion.div
                className="absolute inset-0 block md:hidden"
                style={{
                  background: `linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.4) 55%, rgba(0,0,0,0.1) 100%)`
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              />

              {/* Efecto de partículas flotantes - Reducidas en móvil */}
              <div className="absolute inset-0">
                {[...Array(window.innerWidth < 768 ? 15 : 30)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute rounded-full"
                    style={{
                      width: Math.random() * 4 + 1,
                      height: Math.random() * 4 + 1,
                      background: slides[currentSlide].color,
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      boxShadow: `0 0 ${Math.random() * 20 + 10}px ${slides[currentSlide].color}`
                    }}
                    animate={{
                      y: [0, -100, 0],
                      x: [0, Math.random() * 50 - 25, 0],
                      opacity: [0, 0.7, 0],
                      scale: [0, 1, 0]
                    }}
                    transition={{
                      duration: 3 + Math.random() * 2,
                      repeat: Infinity,
                      delay: Math.random() * 2,
                      ease: "easeInOut"
                    }}
                  />
                ))}
              </div>

              {/* Glitch effect removed for smoother transitions */}
            </motion.div>

            {/* Contenido con animaciones de texto avanzadas */}
            <div className="absolute inset-0 flex items-start md:items-center pt-28 md:pt-0 pb-24 md:pb-0">
              <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full h-full md:h-auto">
                <div className="max-w-3xl h-full md:h-auto flex flex-col justify-start md:block">
                  {/* Badge de marca con efecto glow */}
                  <motion.div
                    initial={{ opacity: 0, y: 30, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -30, scale: 0.8 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="inline-flex items-center gap-2 md:gap-3 px-3 md:px-5 py-2 md:py-2.5 rounded-full mb-4 md:mb-8 relative overflow-hidden"
                    style={{
                      background: `linear-gradient(135deg, ${slides[currentSlide].color}20 0%, ${slides[currentSlide].color}10 100%)`,
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${slides[currentSlide].color}30`
                    }}
                  >
                    <motion.div
                      className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full"
                      style={{ backgroundColor: slides[currentSlide].color }}
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [1, 0.5, 1]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    <span className="text-xs md:text-sm font-bold text-white tracking-wider">
                      {slides[currentSlide].brand}
                    </span>
                    <Sparkles className="h-3 w-3 md:h-4 md:w-4 text-white/60" />

                    {/* Efecto glow animado */}
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: `radial-gradient(circle, ${slides[currentSlide].color}40 0%, transparent 70%)`
                      }}
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [0.5, 0, 0.5]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  </motion.div>

                  {/* Título con efecto split reveal */}
                  <div className="overflow-hidden mb-4 md:mb-6">
                    <motion.h1
                      className="text-4xl sm:text-5xl md:text-7xl lg:text-9xl font-black text-white leading-none tracking-tighter"
                      initial={{ y: 200, rotateX: -90 }}
                      animate={{ y: 0, rotateX: 0 }}
                      exit={{ y: -200, rotateX: 90 }}
                      transition={{
                        duration: 0.8,
                        delay: 0.3,
                        type: "spring",
                        stiffness: 100
                      }}
                      style={{
                        textShadow: `0 0 80px ${slides[currentSlide].color}60,
                                     0 0 40px ${slides[currentSlide].color}40,
                                     0 20px 60px rgba(0,0,0,0.8)`
                      }}
                    >
                      {slides[currentSlide].title}
                    </motion.h1>
                  </div>

                  {/* Subtitle con mask reveal */}
                  <div className="overflow-hidden mb-3 md:mb-4">
                    <motion.p
                      className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light tracking-wide"
                      style={{
                        color: slides[currentSlide].color,
                        textShadow: `0 0 30px ${slides[currentSlide].color}80`
                      }}
                      initial={{ x: -200, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: 200, opacity: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                    >
                      {slides[currentSlide].subtitle}
                    </motion.p>
                  </div>

                  {/* Description con stagger */}
                  <motion.p
                    className="text-sm sm:text-base md:text-lg lg:text-xl text-white/70 mb-6 md:mb-10 font-light tracking-wide"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  >
                    {slides[currentSlide].description}
                  </motion.p>

                  <div className="flex-1 md:hidden"></div>

                  {/* CTAs con hover magnético */}
                  <motion.div
                    className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 md:gap-4"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ duration: 0.5, delay: 0.9 }}
                  >
                    <motion.div
                      whileHover={{ scale: 1.05, y: -5 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-full sm:w-auto"
                    >
                      <a
                        href={`https://wa.me/${slides[currentSlide].phone}?text=${encodeURIComponent(`Hola, me interesa saber más sobre el ${slides[currentSlide].title} y otros modelos disponibles.`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-full sm:w-auto px-6 md:px-8 py-4 md:py-6 text-base md:text-lg font-bold text-white rounded-lg relative overflow-hidden"
                        style={{
                          background: `linear-gradient(135deg, ${slides[currentSlide].color} 0%, ${slides[currentSlide].color}dd 100%)`,
                          boxShadow: `0 10px 40px ${slides[currentSlide].color}40`
                        }}
                      >
                        <span className="relative z-10">Consultá este u otros modelos</span>
                        <motion.div
                          className="absolute inset-0"
                          style={{ background: 'rgba(255,255,255,0.2)' }}
                          initial={{ x: '-100%' }}
                          whileHover={{ x: '100%' }}
                          transition={{ duration: 0.5 }}
                        />
                      </a>
                    </motion.div>

                    {/* Logos Amaya + Marca al lado de los CTAs - Solo en desktop */}
                    <motion.div
                      className="hidden lg:flex items-center gap-4 ml-6 opacity-60 hover:opacity-90 transition-opacity"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 0.6 }}
                      transition={{ delay: 1.1 }}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/logo-amaya.svg`}
                        alt="Amaya Motors"
                        className="h-14 w-auto"
                      />
                      {slides[currentSlide].brandLogo && (
                        <>
                          <div className="h-10 w-px bg-white/40" />
                          <img
                            src={slides[currentSlide].brandLogo}
                            alt={slides[currentSlide].brand}
                            className="h-auto w-auto max-h-9 max-w-[96px] object-contain"
                            style={slides[currentSlide].brandLogoFilter ? { filter: slides[currentSlide].brandLogoFilter } : undefined}
                          />
                        </>
                      )}
                    </motion.div>
                  </motion.div>

                  {/* Logos Amaya + Marca en móvil - Abajo de los botones */}
                  <motion.div
                    className="lg:hidden flex items-center gap-3 mt-6 opacity-80 hover:opacity-100 transition-opacity"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.8 }}
                    transition={{ delay: 1.1 }}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/logo-amaya.svg`}
                      alt="Amaya Motors"
                      className="h-10 w-auto"
                    />
                    {slides[currentSlide].brandLogo && (
                      <>
                        <div className="h-8 w-px bg-white/50" />
                        <img
                          src={slides[currentSlide].brandLogo}
                          alt={slides[currentSlide].brand}
                          className="h-auto w-auto max-h-7 max-w-[80px] object-contain"
                          style={slides[currentSlide].brandLogoFilter ? { filter: slides[currentSlide].brandLogoFilter } : undefined}
                        />
                      </>
                    )}
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Controles del slider con efecto hover */}
        <div className="absolute bottom-6 md:bottom-12 right-4 md:right-12 flex gap-2 md:gap-4 z-30">
          <motion.button
            onClick={prevSlide}
            whileHover={{ scale: 1.1, x: -5 }}
            whileTap={{ scale: 0.9 }}
            className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-2xl border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all text-lg md:text-xl"
          >
            ←
          </motion.button>
          <motion.button
            onClick={nextSlide}
            whileHover={{ scale: 1.1, x: 5 }}
            whileTap={{ scale: 0.9 }}
            className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-2xl border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all text-lg md:text-xl"
          >
            →
          </motion.button>
        </div>

        {/* Indicadores de progreso innovadores */}
        <div className="absolute bottom-6 md:bottom-12 left-1/2 -translate-x-1/2 flex gap-2 md:gap-3 z-30">
          {slides.map((slide, index) => (
            <motion.button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className="relative group"
              whileHover={{ scale: 1.2 }}
            >
              <div className={`h-0.5 md:h-1 rounded-full transition-all ${index === currentSlide ? 'w-10 md:w-16' : 'w-6 md:w-8'
                }`}
                style={{
                  backgroundColor: index === currentSlide ? slide.color : 'rgba(255,255,255,0.3)'
                }} />

              {index === currentSlide && (
                <motion.div
                  className="absolute inset-0 rounded-full"
                  style={{
                    boxShadow: `0 0 20px ${slide.color}, 0 0 40px ${slide.color}80`
                  }}
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 0, 0.5]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              )}
            </motion.button>
          ))}
        </div>

        {/* Scroll indicator con animación - Solo desktop */}
        <motion.div
          className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
          animate={{ y: [0, 15, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="flex flex-col items-center gap-2 text-white/40">
            <span className="text-xs font-medium tracking-widest">SCROLL</span>
            <ChevronDown className="h-5 w-5" />
          </div>
        </motion.div>
      </section>

      {/* Sección de Marcas */}
      <section id="marcas" className="relative py-16 md:py-20 pb-16 md:pb-24" style={{ overflow: 'visible' }}>
        {/* Imagen de fondo */}
        <div className="absolute inset-0" style={{ backgroundImage: `url('${process.env.PUBLIC_URL}/seccion_amaya.png')`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
        {/* Overlay oscuro para legibilidad */}
        <div className="absolute inset-0" style={{ background: 'rgba(5,5,15,0.43)' }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            className="text-center mb-12 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-black text-foreground mb-3">
              Nuestras <span className="text-primary">Marcas</span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Representamos las marcas más innovadoras y confiables del mercado automotriz
            </p>
          </motion.div>

          {/* Grid de marcas - diseño premium con tooltips sofisticados */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {[
              { name: 'BYD', logo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/rqt16dv8_Logo_BYD_Blanco_para_fondo_oscuro.png', tagline: 'Innovación eléctrica', color: '#00d4ff', whatsapp: '59899384307' },
              { name: 'Peugeot', logo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/usltsakg_Logo_Peugeot_Blanco_para_fondo_oscuro.png', tagline: 'Elegancia francesa', color: '#1dd1a1', whatsapp: '59891200262' },
              { name: 'Geely', logo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/925mkif1_Logo_Geely_Blanco_para_fondo_oscuro.png', tagline: 'Tecnología avanzada', color: '#7aa8b7', whatsapp: '59891748578' },
              { name: 'Riddara', logo: 'https://www.riddara.com/-/media/images/header/logo-update/left-logo-white.svg', tagline: 'Pickup eléctrico', color: '#ff6b35', whatsapp: '59891200262' },
              { name: 'Jetour', logo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/0zf9gbf9_Logo_Jetour_Blanco_para_fondo_oscuro.png', tagline: 'Aventura sin límites', color: '#f39c12', whatsapp: '59891748578' },
              { name: 'Soueast', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Soueast_Logo.png', tagline: 'Movimiento urbano', color: '#e8a020', whatsapp: '59891748578' },
              { name: 'GWM', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d2/GWM_2025_logo.svg/500px-GWM_2025_logo.svg.png', tagline: 'Fuerza y prestación', color: '#c0392b', whatsapp: '59891748578' },
              { name: 'JAC', logo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/hoy24lx3_Logo_Jac_Blanco_para_fondo_oscuro.png', tagline: 'Calidad confiable', color: '#3498db', whatsapp: '59891748578' },
              { name: 'Dong Feng', logo: 'https://upload.wikimedia.org/wikipedia/commons/e/eb/Dongfeng_Motor_logo.svg', tagline: 'Legado industrial', color: '#e60021', whatsapp: '59891748578' },
              { name: 'Karry', logo: 'https://customer-assets.emergentagent.com/job_darkmode-vehicles/artifacts/y1p4rxta_Logo_Karry_Blanco_para_fondo_oscuro.png', tagline: 'Soluciones comerciales', color: '#9b59b6', whatsapp: '59891748578' },
            ].map((brand, index) => (
              <motion.div
                key={brand.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setHoveredBrand(hoveredBrand === brand.name ? null : brand.name);
                }}
                className="cursor-pointer relative brand-tooltip-trigger"
                style={{
                  transform: hoveredBrand === brand.name ? 'scale(1.05) translateY(-5px)' : 'scale(1) translateY(0)',
                  transition: 'transform 0.3s ease'
                }}
              >
                <div
                  className="relative p-6 md:p-8 rounded-2xl transition-all duration-300 h-full flex items-center justify-center min-h-[120px]"
                  style={{
                    background: theme === 'dark'
                      ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                      : 'linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(241,245,249,0.7) 100%)',
                    border: theme === 'dark'
                      ? '1px solid rgba(255,255,255,0.1)'
                      : '1px solid rgba(0,0,0,0.06)',
                    boxShadow: theme === 'light'
                      ? '0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)'
                      : 'none'
                  }}
                >
                  <img
                    src={brand.logo}
                    alt={brand.name}
                    className="w-full h-auto max-h-10 md:max-h-12 object-contain transition-all duration-300"
                    style={{
                      filter: theme === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0)',
                      opacity: hoveredBrand === brand.name
                        ? '1'
                        : (theme === 'light' ? '0.95' : '0.4')
                    }}
                  />
                  {/* Efecto glow sutil en hover */}
                  <div
                    className="absolute inset-0 rounded-2xl transition-colors duration-300"
                    style={{
                      background: theme === 'dark'
                        ? 'transparent'
                        : 'transparent'
                    }}
                  />
                </div>

                {/* Tooltip Ultra-Refinado - Click/Tap para abrir, FIJO hasta cerrar */}
                <AnimatePresence>
                  {hoveredBrand === brand.name && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="brand-tooltip-content absolute top-full left-1/2 mt-6"
                      style={{
                        pointerEvents: 'auto',
                        zIndex: 9999,
                        transform: 'translateX(-50%)',
                        position: 'absolute'
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseEnter={(e) => e.stopPropagation()}
                      onMouseLeave={(e) => e.stopPropagation()}
                      onMouseMove={(e) => e.stopPropagation()}
                    >
                      <div className="relative">
                        {/* Flecha superior con glassmorphism */}
                        <div
                          className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 backdrop-blur-xl border-l border-t rotate-45 shadow-lg bg-popover"
                          style={{
                            borderColor: brand.color + '40'
                          }}
                        />

                        {/* Contenedor principal del tooltip */}
                        <div
                          className="relative backdrop-blur-2xl rounded-2xl shadow-2xl min-w-[260px] overflow-hidden bg-popover border"
                          style={{
                            borderColor: theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
                            boxShadow: theme === 'dark'
                              ? '0 20px 60px rgba(0,0,0,0.8), 0 4px 12px rgba(0,0,0,0.5)'
                              : '0 20px 60px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.08)'
                          }}
                        >

                          {/* Header con marca y tagline */}
                          <div className="relative px-5 py-4 border-b" style={{ borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                            <h3 className="text-foreground font-bold text-lg mb-1">{brand.name}</h3>
                            <p className="text-sm text-muted-foreground">{brand.tagline}</p>
                          </div>

                          {/* Opciones del menú */}
                          <div className="relative p-3 space-y-1">
                            <a
                              href="#0km"
                              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground transition-all duration-200"
                              style={{
                                background: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
                                e.currentTarget.style.transform = 'translateX(5px)';
                              }}
                              onMouseLeave={(e) => {
                                e.stopPropagation();
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.transform = 'translateX(0)';
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setHoveredBrand(null);
                              }}
                            >
                              <Car className="h-4 w-4 relative z-10 text-muted-foreground" />
                              <span className="text-sm font-medium relative z-10">Ver {brand.name} 0km</span>
                            </a>

                            <a
                              href="#usados"
                              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground transition-all duration-200"
                              style={{
                                background: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
                                e.currentTarget.style.transform = 'translateX(5px)';
                              }}
                              onMouseLeave={(e) => {
                                e.stopPropagation();
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.transform = 'translateX(0)';
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setHoveredBrand(null);
                              }}
                            >
                              <ShoppingCart className="h-4 w-4 relative z-10 text-muted-foreground" />
                              <span className="text-sm font-medium relative z-10">Ver {brand.name} Usados</span>
                            </a>

                            <div
                              className="h-px my-2"
                              style={{
                                background: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                              }}
                            />

                            <a
                              href="#service"
                              className="flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground hover:text-foreground transition-all duration-200"
                              style={{
                                background: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
                                e.currentTarget.style.transform = 'translateX(5px)';
                              }}
                              onMouseLeave={(e) => {
                                e.stopPropagation();
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.transform = 'translateX(0)';
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setHoveredBrand(null);
                              }}
                            >
                              <Wrench className="h-4 w-4 relative z-10 text-muted-foreground" />
                              <span className="text-sm relative z-10">Agendar Service</span>
                            </a>

                            <a
                              href={`https://wa.me/${brand.whatsapp}?text=${encodeURIComponent(`Hola, quisiera información sobre vehículos ${brand.name}.`)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200"
                              style={{
                                background: 'transparent',
                                color: '#25D366'
                              }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                e.currentTarget.style.background = 'rgba(37,211,102,0.08)';
                                e.currentTarget.style.transform = 'translateX(5px)';
                              }}
                              onMouseLeave={(e) => {
                                e.stopPropagation();
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.transform = 'translateX(0)';
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setHoveredBrand(null);
                              }}
                            >
                              <svg className="h-4 w-4 relative z-10 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                              <span className="text-sm font-medium relative z-10">Contactar agente comercial</span>
                            </a>
                          </div>

                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sección de Recomendados */}
      {!loadingFeatured && featuredVehicles.length > 0 && (
        <section id="recomendados" className="relative py-12 md:py-20 bg-gradient-to-b from-background via-muted/30 to-background transition-colors duration-500" style={{ zIndex: 1 }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              className="text-center mb-10 md:mb-14"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-black text-foreground mb-3">
                Vehículos <span className="text-primary">Destacados</span>
              </h2>
              <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
                Las mejores opciones del mercado seleccionadas para vos
              </p>
            </motion.div>

            {/* Grid de autos - diseño compacto */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              {featuredVehicles.map((car, index) => (
                <motion.div
                  key={car.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  whileHover={{ y: -5 }}
                  className="group cursor-pointer"
                >
                  <Link to={`/vehiculos/${car.id}`}>
                    <div
                      className="relative overflow-hidden rounded-2xl transition-all duration-500"
                      style={{
                        background: theme === 'dark'
                          ? 'linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)'
                          : 'linear-gradient(135deg, rgba(248,250,252,0.9) 0%, rgba(241,245,249,0.8) 100%)',
                        border: theme === 'dark'
                          ? '1px solid rgba(255,255,255,0.1)'
                          : '1px solid rgba(0,0,0,0.08)',
                        boxShadow: theme === 'light'
                          ? '0 4px 12px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.03)'
                          : 'none'
                      }}
                    >
                      {/* Layout horizontal compacto */}
                      <div className="flex flex-col lg:flex-row h-full">
                        {/* Imagen a la izquierda */}
                        <div className="relative lg:w-2/5 h-48 lg:h-auto overflow-hidden">
                          <img
                            src={car.image}
                            alt={car.name}
                            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-transparent to-transparent" />

                          {/* Badge de año */}
                          <div
                            className="absolute top-4 left-4 px-3 py-1.5 rounded-full backdrop-blur-xl border text-xs font-medium"
                            style={{
                              background: theme === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)',
                              borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.2)',
                              color: theme === 'dark' ? 'white' : 'black'
                            }}
                          >
                            {car.year}
                          </div>

                          {/* Efecto glow en hover */}
                          <div className="absolute inset-0 bg-gradient-to-t from-primary/0 group-hover:from-primary/10 transition-all duration-500" />
                        </div>

                        {/* Contenido a la derecha - más compacto */}
                        <div className="lg:w-3/5 p-5 lg:p-6 flex flex-col justify-between">
                          <div className="flex-grow">
                            {/* Header */}
                            <div className="mb-3">
                              <div
                                className="text-xs uppercase tracking-wider mb-1 font-medium line-clamp-1"
                                style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}
                              >
                                {car.category}
                              </div>
                              <h3
                                className="text-[1.1rem] lg:text-xl font-black mb-2 group-hover:text-primary transition-colors duration-300 line-clamp-2 leading-tight"
                                style={{ color: theme === 'dark' ? 'white' : 'black' }}
                              >
                                {car.name}
                              </h3>
                            </div>

                            {/* Specs técnicos en grid - más compacto */}
                            <div className="grid grid-cols-2 gap-2 mb-3">
                              <div className="space-y-0.5">
                                <div
                                  className="text-[10px] uppercase tracking-wider"
                                  style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}
                                >Motor</div>
                                <div
                                  className="text-xs font-medium truncate"
                                  style={{ color: theme === 'dark' ? 'white' : 'black' }}
                                >{car.specs.motor}</div>
                              </div>
                              {car.specs.autonomia !== 'N/A' && (
                                <div className="space-y-0.5">
                                  <div
                                    className="text-[10px] uppercase tracking-wider"
                                    style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}
                                  >Autonomía</div>
                                  <div
                                    className="text-xs font-medium truncate"
                                    style={{ color: theme === 'dark' ? 'white' : 'black' }}
                                  >{car.specs.autonomia}</div>
                                </div>
                              )}
                              {car.specs.bateria !== 'N/A' && car.specs.bateria !== '' && (
                                <div className="space-y-0.5">
                                  <div
                                    className="text-[10px] uppercase tracking-wider"
                                    style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}
                                  >Batería</div>
                                  <div
                                    className="text-xs font-medium truncate"
                                    style={{ color: theme === 'dark' ? 'white' : 'black' }}
                                  >{car.specs.bateria}</div>
                                </div>
                              )}
                              <div className="space-y-0.5">
                                <div
                                  className="text-[10px] uppercase tracking-wider"
                                  style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}
                                >Tracción</div>
                                <div
                                  className="text-xs font-medium truncate"
                                  style={{ color: theme === 'dark' ? 'white' : 'black' }}
                                >{car.specs.traccion}</div>
                              </div>
                            </div>
                          </div>

                          {/* Características destacadas - más compacto */}
                          <div className="mb-3">
                            <div className="flex flex-wrap gap-1.5">
                              {car.specs.extras.slice(0, 3).map((extra, i) => (
                                <div
                                  key={i}
                                  className="px-2 py-1 rounded-full border text-[10px] truncate max-w-[120px]"
                                  style={{
                                    background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                    borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)',
                                    color: theme === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'
                                  }}
                                >
                                  {extra}
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Footer con precio y CTA - compacto */}
                          <div
                            className="pt-3 border-t mt-auto"
                            style={{ borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)' }}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <div
                                  className="text-[10px] uppercase tracking-wider"
                                  style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}
                                >Desde</div>
                                <div
                                  className="text-lg lg:text-xl font-black"
                                  style={{ color: theme === 'dark' ? 'white' : 'black' }}
                                >
                                  {car.price.split(' ')[0]} <span className="text-primary">{car.price.split(' ')[1]}</span>
                                </div>
                              </div>
                              <Button
                                className="backdrop-blur-xl px-4 py-2 text-sm whitespace-nowrap pointer-events-none"
                                style={{
                                  background: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                  color: theme === 'dark' ? 'white' : 'black',
                                  borderWidth: '1px',
                                  borderColor: theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.3)'
                                }}
                              >
                                Más info
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Talleres – Agenda Service */}
      <section id="service" className="relative py-16 md:py-24">
        <div className="absolute inset-0" style={{ backgroundImage: `url('${process.env.PUBLIC_URL}/Taller_agenda_con_sistema_de_IA_Artificialmente.png')`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0" style={{ background: 'rgba(5,5,15,0.45)' }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            className="text-center mb-12 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-5 text-xs font-semibold uppercase tracking-widest"
              style={{ borderColor: 'rgba(59,130,246,0.25)', background: theme === 'dark' ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.05)', color: 'rgba(96,165,250,1)' }}
            >
              <Wrench className="w-3.5 h-3.5" />
              Talleres Oficiales
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-foreground mb-3">
              Agenda tu <span className="text-primary">Service</span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto">
              Técnicos especializados y tecnología de diagnóstico de última generación en todos nuestros talleres
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { id: 'costa', nombre: 'Suc. Costa', direccion: 'Av. Giannattasio Km 21.500, Ciudad de la Costa', telefono: '092 487 711', email: 'amaya.peugeot@gmail.com', horario: 'Lun–Vie 9:00–13:00 / 14:00–18:30 · Sáb 9:00–13:00' },
              { id: 'propios', nombre: 'Suc. Propios', direccion: 'Bvar. Batlle y Ordóñez 3848', telefono: '092 931 302', email: 'amaya.motors@amaya.com.uy', horario: 'Lun–Vie 9:00–12:30 / 13:30–18:30 · Sáb 9:00–13:00' },
              { id: 'nuevocentro', nombre: 'Suc. Nuevocentro', direccion: 'Luis Alberto de Herrera 3428', telefono: '092 394 511', email: 'amaya.byd@gmail.com', horario: 'Lun–Vie 9:00–13:00 / 14:00–18:30 · Sáb 9:00–13:00' },
            ].map((taller, index) => (
              <motion.div
                key={taller.nombre}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="relative flex flex-col gap-5 rounded-2xl p-6"
                style={{
                  background: theme === 'dark'
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                    : 'linear-gradient(135deg, rgba(248,250,252,0.95) 0%, rgba(241,245,249,0.8) 100%)',
                  border: theme === 'dark'
                    ? '1px solid rgba(255,255,255,0.08)'
                    : '1px solid rgba(0,0,0,0.06)',
                  boxShadow: theme === 'light' ? '0 4px 16px rgba(0,0,0,0.06)' : 'none'
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)' }}>
                    <Wrench className="w-5 h-5 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">{taller.nombre}</h3>
                </div>
                <div className="space-y-3 flex-grow">
                  <a
                    href={`https://maps.google.com?q=${encodeURIComponent(taller.direccion + ', Uruguay')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3"
                    style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)' }}
                  >
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-blue-400" />
                    <span className="text-sm hover:text-foreground transition-colors">{taller.direccion}</span>
                  </a>
                  <a
                    href={`tel:${taller.telefono.replace(/\s/g, '')}`}
                    className="flex items-center gap-3"
                    style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)' }}
                  >
                    <Phone className="w-4 h-4 shrink-0 text-blue-400" />
                    <span className="text-sm hover:text-foreground transition-colors">{taller.telefono}</span>
                  </a>
                  <a
                    href={`mailto:${taller.email}`}
                    className="flex items-center gap-3"
                    style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)' }}
                  >
                    <Mail className="w-4 h-4 shrink-0 text-blue-400" />
                    <span className="text-sm hover:text-foreground transition-colors lowercase">{taller.email}</span>
                  </a>
                  {taller.horario && (
                    <div className="flex items-start gap-3" style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)' }}>
                      <Clock className="w-4 h-4 mt-0.5 shrink-0 text-blue-400" />
                      <span className="text-sm">{taller.horario}</span>
                    </div>
                  )}
                </div>
                <Link
                  to={`/agendar?taller=${taller.id}`}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95"
                  style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', boxShadow: '0 4px 16px rgba(59,130,246,0.3)' }}
                >
                  <Calendar className="w-4 h-4" />
                  Agendar Service
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sucursales de Venta */}
      <section id="sucursales" className="relative py-16 md:py-24 bg-background transition-colors duration-500">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: theme === 'dark'
              ? 'linear-gradient(to bottom, rgba(255,255,255,0.01) 0%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.01) 100%)'
              : 'linear-gradient(to bottom, rgba(0,0,0,0.01) 0%, rgba(0,0,0,0.025) 50%, rgba(0,0,0,0.01) 100%)'
          }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            className="text-center mb-12 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-5 text-xs font-semibold uppercase tracking-widest"
              style={{
                borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                color: theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)'
              }}
            >
              <MapPin className="w-3.5 h-3.5" />
              8 Puntos de Venta
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-foreground mb-3">
              Nuestras <span className="text-primary">Sucursales</span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto">
              Encontranos en Montevideo, La Costa y Punta del Este
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { nombre: 'BYD Propios', telefono: '099 384 307', direccion: 'Bvar. Batlle y Ordóñez 3425', email: 'benjaminparra.amaya@gmail.com', horario: 'Lun–Vie 9:00–12:30 / 13:30–18:30 · Sáb 9:00–13:00' },
              { nombre: 'Nuevocentro', telefono: '091 200 262', direccion: 'Luis Alberto de Herrera 3428', email: 'amaya.byd@gmail.com', horario: 'Lun–Vie 9:00–18:30 · Sáb 9:00–13:00' },
              { nombre: 'La Costa', telefono: '094 439 755', direccion: 'Av. Giannattasio Km 21.500 esq. Becú', email: 'amaya.peugeot@gmail.com', horario: 'Lun–Vie 9:00–13:00 / 14:00–18:30 · Sáb 9:00–13:00' },
              { nombre: 'Amaya Seleccionados', telefono: '091 748 578', direccion: 'Bvar. Batlle y Ordóñez 3511', email: null },
              { nombre: 'Amaya Usados', telefono: '091 214 809', direccion: 'Bvar. Batlle y Ordóñez 3870', email: null },
              { nombre: 'Amaya Pocitos', telefono: '2706 7268', direccion: 'Bartolito Mitre 2782', email: null },
              { nombre: 'Amaya Puente', telefono: '2601 5857', direccion: 'Av. Giannattasio 7712', email: null },
              { nombre: 'Amaya Punta del Este', telefono: '093 967 803', direccion: 'Nilo Berreta, Punta del Este', email: null },
            ].map((sucursal, index) => (
              <motion.div
                key={sucursal.nombre}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -3 }}
                className="relative rounded-2xl p-5 flex flex-col gap-3"
                style={{
                  background: theme === 'dark'
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)'
                    : 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.9) 100%)',
                  border: theme === 'dark'
                    ? '1px solid rgba(255,255,255,0.07)'
                    : '1px solid rgba(0,0,0,0.06)',
                  boxShadow: theme === 'light' ? '0 2px 8px rgba(0,0,0,0.04)' : 'none'
                }}
              >
                <h3 className="text-sm font-bold text-foreground">{sucursal.nombre}</h3>
                <div className="space-y-2">
                  <a
                    href={`https://maps.google.com?q=${encodeURIComponent(sucursal.direccion + ', Uruguay')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2"
                    style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                  >
                    <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary" />
                    <span className="text-xs hover:text-foreground transition-colors">{sucursal.direccion}</span>
                  </a>
                  <a
                    href={`tel:${sucursal.telefono.replace(/\s/g, '')}`}
                    className="flex items-center gap-2"
                    style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                  >
                    <Phone className="w-3.5 h-3.5 shrink-0 text-primary" />
                    <span className="text-xs hover:text-foreground transition-colors">{sucursal.telefono}</span>
                  </a>
                  {sucursal.email && (
                    <a
                      href={`mailto:${sucursal.email}`}
                      className="flex items-center gap-2"
                      style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}
                    >
                      <Mail className="w-3.5 h-3.5 shrink-0 text-primary" />
                      <span className="text-xs hover:text-foreground transition-colors lowercase truncate">{sucursal.email}</span>
                    </a>
                  )}
                  {sucursal.horario && (
                    <div className="flex items-start gap-2" style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                      <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary" />
                      <span className="text-xs">{sucursal.horario}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Seguros */}
      <section id="seguros" className="relative py-16 md:py-24 overflow-hidden">
        {/* Imagen de fondo */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/hero/Geely_EX5_Desktop.jpg')" }}
        />
        {/* Overlay oscuro */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.65) 50%, rgba(0,0,0,0.75) 100%)' }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* Columna izquierda */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-px" style={{ background: 'rgba(96,165,250,0.7)' }} />
                <Shield className="w-4 h-4" style={{ color: 'rgba(147,197,253,0.9)' }} />
                <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: 'rgba(147,197,253,0.9)' }}>Seguros</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight">
                Asegurá tu vehículo<br />
                <span className="text-blue-400">con nosotros</span>
              </h2>
              <p className="text-base md:text-lg mb-8 max-w-md" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Cotizá tu seguro en minutos. Te asesoramos para encontrar la cobertura ideal con las mejores compañías del mercado uruguayo.
              </p>
              <ul className="space-y-3">
                {['Cotización sin compromiso', 'Múltiples compañías aseguradoras', 'Asesoramiento personalizado', 'Gestión de siniestros incluida'].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 shrink-0 text-blue-400" />
                    <span className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Formulario */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-2xl p-6 md:p-8"
              style={{
                background: 'rgba(15,20,30,0.85)',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                backdropFilter: 'blur(12px)'
              }}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-white">Solicitá tu cotización</h3>
                <img
                  src={`${process.env.PUBLIC_URL}/logo-amaya.svg`}
                  alt="Amaya Motors"
                  style={{ height: '28px', width: 'auto', opacity: 0.3, filter: 'brightness(10)' }}
                />
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Nombre <span className="text-blue-400">*</span></label>
                    <input
                      type="text"
                      placeholder="Tu nombre"
                      value={seguroForm.nombre}
                      onChange={e => setSeguroForm(f => ({ ...f, nombre: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-xl text-sm outline-none transition-all placeholder:text-white/30"
                      style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'white' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Teléfono <span className="text-blue-400">*</span></label>
                    <input
                      type="tel"
                      placeholder="09x xxx xxx"
                      value={seguroForm.telefono}
                      onChange={e => setSeguroForm(f => ({ ...f, telefono: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-xl text-sm outline-none transition-all placeholder:text-white/30"
                      style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'white' }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Email</label>
                  <input
                    type="email"
                    placeholder="tu@email.com"
                    value={seguroForm.email}
                    onChange={e => setSeguroForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-xl text-sm outline-none transition-all placeholder:text-white/30"
                    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'white' }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Marca</label>
                    <input
                      type="text"
                      placeholder="BYD"
                      value={seguroForm.marca}
                      onChange={e => setSeguroForm(f => ({ ...f, marca: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-xl text-sm outline-none transition-all placeholder:text-white/30"
                      style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'white' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Modelo</label>
                    <input
                      type="text"
                      placeholder="Yuan Pro"
                      value={seguroForm.modelo}
                      onChange={e => setSeguroForm(f => ({ ...f, modelo: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-xl text-sm outline-none transition-all placeholder:text-white/30"
                      style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'white' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Año</label>
                    <input
                      type="text"
                      placeholder="2024"
                      value={seguroForm.anio}
                      onChange={e => setSeguroForm(f => ({ ...f, anio: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-xl text-sm outline-none transition-all placeholder:text-white/30"
                      style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'white' }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Cobertura deseada</label>
                  <select
                    value={seguroForm.cobertura}
                    onChange={e => setSeguroForm(f => ({ ...f, cobertura: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-xl text-sm outline-none transition-all appearance-none"
                    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: seguroForm.cobertura ? 'white' : 'rgba(255,255,255,0.3)' }}
                  >
                    <option value="" disabled style={{ background: '#1e293b', color: 'rgba(255,255,255,0.4)' }}>Seleccioná una opción</option>
                    <option value="Todo Riesgo" style={{ background: '#1e293b', color: 'white' }}>Todo Riesgo</option>
                    <option value="Terceros Completo" style={{ background: '#1e293b', color: 'white' }}>Terceros Completo</option>
                    <option value="Terceros Básico" style={{ background: '#1e293b', color: 'white' }}>Terceros Básico</option>
                    <option value="No sé, quiero asesoramiento" style={{ background: '#1e293b', color: 'white' }}>No sé, quiero asesoramiento</option>
                  </select>
                </div>
                {seguroEnvio === 'ok' ? (
                  <div className="text-center py-4">
                    <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
                    <p className="text-white font-semibold">¡Solicitud enviada!</p>
                    <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Te contactamos a la brevedad.</p>
                  </div>
                ) : (
                  <>
                    <button
                      disabled={seguroEnvio === 'loading' || !seguroForm.nombre || !seguroForm.telefono}
                      onClick={async () => {
                        setSeguroEnvio('loading');
                        try {
                          const res = await fetch('/api/seguros/cotizacion', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(seguroForm)
                          });
                          if (res.ok) {
                            setSeguroEnvio('ok');
                          } else {
                            throw new Error('error');
                          }
                        } catch {
                          setSeguroEnvio('idle');
                          window.open(`https://wa.me/59892931302?text=${encodeURIComponent(`Hola Amaya, quisiera cotizar un seguro.\n\nNombre: ${seguroForm.nombre}\nTeléfono: ${seguroForm.telefono}${seguroForm.email ? `\nEmail: ${seguroForm.email}` : ''}${seguroForm.marca ? `\nVehículo: ${seguroForm.marca} ${seguroForm.modelo} ${seguroForm.anio}` : ''}${seguroForm.cobertura ? `\nCobertura: ${seguroForm.cobertura}` : ''}`)}`, '_blank');
                        }
                      }}
                      className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', boxShadow: '0 4px 16px rgba(37,211,102,0.3)' }}
                    >
                      {seguroEnvio === 'loading' ? (
                        <span>Enviando...</span>
                      ) : (
                        <>
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.121 1.532 5.851L.057 23.743a.75.75 0 0 0 .916.916l5.892-1.475A11.952 11.952 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22.5a10.457 10.457 0 0 1-5.364-1.476l-.385-.228-3.993.999.999-3.993-.228-.385A10.457 10.457 0 0 1 1.5 12C1.5 6.201 6.201 1.5 12 1.5S22.5 6.201 22.5 12 17.799 22.5 12 22.5z"/></svg>
                          Solicitar cotización
                        </>
                      )}
                    </button>
                    <p className="text-center text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Te contactamos a la brevedad · Sin compromiso</p>
                  </>
                )}
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Footer Profesional */}
      <footer id="contacto" className="relative bg-black border-t transition-colors duration-500" style={{ borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

            {/* Column 1: Logo & Description */}
            <div className="lg:col-span-1">
              <Link to="/" className="inline-block mb-6">
                <img
                  src={`${process.env.PUBLIC_URL}/logo-amaya.svg`}
                  alt="Amaya Motors"
                  className="h-10 w-auto"
                />
              </Link>
              <p className="text-white/60 text-sm leading-relaxed mb-6">
                Concesionario oficial de las mejores marcas. Más de 20 años acompañando a familias uruguayas en la elección de su vehículo ideal.
              </p>
              {/* Social Links */}
              <div className="flex gap-3">
                <a
                  href="https://www.facebook.com/profile.php?id=61584072839314#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                >
                  <Facebook className="h-4 w-4" />
                </a>
                <a
                  href="https://www.instagram.com/amaya.nuevocentro/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                >
                  <Instagram className="h-4 w-4" />
                </a>
                <a
                  href="https://www.youtube.com/@amayamotors7067"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                >
                  <Youtube className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Column 2: Quick Links */}
            <div>
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">Navegación</h4>
              <ul className="space-y-3">
                {[
                  { label: 'Inicio', href: '/', isLink: true },
                  { label: 'Vehículos', href: '/vehiculos', isLink: true },
                  { label: 'Nuestras Marcas', href: '#marcas' },
                  { label: 'Sucursales', href: '#sucursales' },
                  { label: 'Contacto', href: '#contacto' }
                ].map((item) => (
                  <li key={item.label}>
                    {item.isLink ? (
                      <Link
                        to={item.href}
                        className="text-white/60 hover:text-white text-sm transition-colors duration-300 flex items-center gap-2 group"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/50 group-hover:bg-primary transition-colors" />
                        {item.label}
                      </Link>
                    ) : (
                      <a
                        href={item.href}
                        className="text-white/60 hover:text-white text-sm transition-colors duration-300 flex items-center gap-2 group"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/50 group-hover:bg-primary transition-colors" />
                        {item.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3: Services */}
            <div>
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">Servicios</h4>
              <ul className="space-y-3">
                {[
                  { label: 'Vehículos 0km', href: '#0km' },
                  { label: 'Vehículos Usados', href: '#usados' },
                  { label: 'Service Oficial', href: '#service' },
                  { label: 'Repuestos', href: '#repuestos' },
                  { label: 'Financiación', href: '#financiacion' }
                ].map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className="text-white/60 hover:text-white text-sm transition-colors duration-300 flex items-center gap-2 group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-500/50 group-hover:bg-cyan-500 transition-colors" />
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 4: Contact Info */}
            <div>
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">Contacto</h4>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-4">Suc. Nuevocentro</p>
              <ul className="space-y-4">
                <li>
                  <a
                    href="mailto:amaya.byd@gmail.com"
                    className="flex items-start gap-3 text-white/60 hover:text-white transition-colors duration-300 group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-primary/30 group-hover:bg-primary/10 transition-all">
                      <Mail className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="text-xs text-white/40 block">Email</span>
                      <span className="text-sm">amaya.byd@gmail.com</span>
                    </div>
                  </a>
                </li>
                <li>
                  <a
                    href="tel:092394511"
                    className="flex items-start gap-3 text-white/60 hover:text-white transition-colors duration-300 group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-primary/30 group-hover:bg-primary/10 transition-all">
                      <Phone className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="text-xs text-white/40 block">Teléfono</span>
                      <span className="text-sm">092 394 511</span>
                    </div>
                  </a>
                </li>
                <li>
                  <a
                    href="https://maps.google.com?q=Luis+Alberto+de+Herrera+3428,+Montevideo,+Uruguay"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 text-white/60 hover:text-white transition-colors duration-300 group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-primary/30 group-hover:bg-primary/10 transition-all">
                      <MapPin className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="text-xs text-white/40 block">Dirección</span>
                      <span className="text-sm">Luis Alberto de Herrera 3428<br />Montevideo, Uruguay</span>
                    </div>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="py-6 border-t border-white/10 flex flex-col items-center gap-5">
            <p className="text-white/40 text-xs">
              © {new Date().getFullYear()} Amaya Motors. Todos los derechos reservados.
            </p>
            <div className="flex flex-col items-center gap-1.5">
              <a href="https://artificialmente.uy" target="_blank" rel="noopener noreferrer">
                <img
                  src={`${process.env.PUBLIC_URL}/logo-artificialmente.svg`}
                  alt="artificialmente"
                  style={{ height: '22px', width: 'auto', opacity: 0.5 }}
                />
              </a>
              <span className="text-white/25 text-[10px] tracking-widest uppercase">Inteligencia empresarial</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="#" className="text-white/40 hover:text-white/60 text-xs transition-colors">
                Términos y Condiciones
              </a>
              <a href="#" className="text-white/40 hover:text-white/60 text-xs transition-colors">
                Política de Privacidad
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingVanguard;
